<template>
  <form
    class="v3-form behaviour-point-correction-form"
    data-testid="behaviour-point-correction-form"
    @submit.prevent="onSubmit"
  >
    <div class="mb-3">
      <Label
        required
        label-id="behaviour-correction-points-lbl"
        html-for="behaviour-correction-points-input"
      >
        {{ fieldLabels.pointsUsed }}
      </Label>
      <p id="behaviour-correction-points-hint" class="text-danger small mb-2">
        {{ pointsCorrectedHint }}
      </p>
      <InputField
        id="behaviour-correction-points-input"
        label-id="behaviour-correction-points-lbl"
        class="w-100"
        input-type="number"
        :model-value="state.form.points"
        :invalid-feedback="errors.points"
        data-testid="behaviour-correction-points-input"
        @update:model-value="(v) => patchForm('points', v)"
      />
    </div>

    <div class="mb-3">
      <Label
        required
        label-id="behaviour-correction-reason-lbl"
        html-for="behaviour-correction-reason-input"
      >
        {{ $t("behaviour_manage.correction_reason_label") }}
      </Label>
      <InputField
        id="behaviour-correction-reason-input"
        label-id="behaviour-correction-reason-lbl"
        class="w-100"
        type="textarea"
        :rows="4"
        :model-value="state.form.reason"
        :invalid-feedback="errors.reason"
        data-testid="behaviour-correction-reason-input"
        @update:model-value="(v) => patchForm('reason', v)"
      />
    </div>

    <BehaviourSelectStudentsFieldset
      v-model="state.form.studentIds"
      v-model:selection-method="state.form.selectionMethod"
      v-model:csv-file="state.form.csvFile"
      class="mb-3"
      allow-csv-upload
      :invalid-feedback="errors.studentIds"
      :csv-invalid-feedback="errors.csvFile"
      label-id="behaviour-correction-students-lbl"
      input-id="behaviour-correction-students-input"
      data-testid="behaviour-correction-students-select"
      @csv-error="onCsvError"
    />

    <InfoBox
      v-if="state.serverMessage"
      :class="['mb-3', { danger: state.isServerError }]"
      :title="state.serverTitle"
      :message="state.serverMessage"
      announce-assertive
    />

    <div class="text-center">
      <LoadingButton
        class="me-2 px-4"
        type="button"
        rounded
        data-testid="behaviour-correction-cancel"
        @click="onCancel"
      >
        {{ $t("common.cancel") }}
      </LoadingButton>
      <LoadingButton
        class="px-4"
        type="submit"
        solid
        rounded
        :is-loading="state.isProcessing"
        data-testid="behaviour-correction-submit"
      >
        {{ $t("behaviour_manage.correction_submit") }}
      </LoadingButton>
    </div>
  </form>
</template>

<script>
import BehaviourSelectStudentsFieldset from "@/v3components/Forms/Behaviour/BehaviourSelectStudentsFieldset.vue"
import behaviourPointsConstants from "@/constants/behaviourPointsConstants.js"
import InfoBox from "@/v3components/shared/Alerts/InfoBox.vue"
import InputField from "@/v3components/shared/Form/InputField.vue"
import Label from "@/v3components/shared/Form/Label.vue"
import LoadingButton from "@/v3components/shared/Buttons/LoadingButton.vue"
import useAppToast from "@/composables/useAppToast.js"
import {
  normalizeBehaviourStudentIds,
  parseBehaviourPointsApiError
} from "@/helpers/behaviourPointsForm.js"
import { claimPbisCsvCompletionToast } from "@/helpers/behaviourPbisSocket.js"
import { computed, inject, reactive } from "vue"
import { useI18n } from "vue-i18n"
import { useStore } from "vuex"

const { STUDENT_SELECTION_METHOD } = behaviourPointsConstants

export default {
  name: "BehaviourPointCorrectionForm",
  components: {
    BehaviourSelectStudentsFieldset,
    InfoBox,
    InputField,
    Label,
    LoadingButton
  },
  emits: ["cancel"],
  setup(props, { emit }) {
    const { t } = useI18n()
    const store = useStore()
    const modal = inject("modal")
    const toast = useAppToast()

    const behaviourTerminology = computed(
      () => store.getters["behaviourSettings/behaviourTerminology"]
    )

    const fieldLabels = computed(() => ({
      pointsUsed: t("behaviour_manage.points_corrected_label", {
        points: behaviourTerminology.value.points
      })
    }))

    const pointsCorrectedHint = computed(() =>
      t("behaviour_manage.points_corrected_hint", {
        points: behaviourTerminology.value.points
      })
    )

    const state = reactive({
      form: {
        points: "",
        reason: "",
        studentIds: [],
        selectionMethod: STUDENT_SELECTION_METHOD.MULTISELECT,
        csvFile: null
      },
      apiFieldErrors: {},
      csvClientError: "",
      isProcessing: false,
      isServerError: false,
      serverTitle: "",
      serverMessage: "",
      touched: false
    })

    const isCsvSelection = computed(
      () => state.form.selectionMethod === STUDENT_SELECTION_METHOD.CSV
    )

    const clientErrors = computed(() => {
      if (!state.touched) return {}
      const out = {}
      if (!String(state.form.points ?? "").trim()) {
        out.points = t("behaviour_manage.error_required_field", {
          field: fieldLabels.value.pointsUsed
        })
      }
      if (!String(state.form.reason ?? "").trim()) {
        out.reason = t("behaviour_manage.error_required_field", {
          field: t("behaviour_manage.correction_reason_label")
        })
      }
      if (isCsvSelection.value) {
        if (!state.form.csvFile) {
          out.csvFile =
            state.csvClientError ||
            t("behaviour_manage.error_csv_file_required")
        }
      } else if (!normalizeBehaviourStudentIds(state.form.studentIds).length) {
        out.studentIds = t("behaviour_manage.error_select_students")
      }
      return out
    })

    const errors = computed(() => ({
      ...clientErrors.value,
      ...state.apiFieldErrors,
      ...(state.csvClientError && isCsvSelection.value
        ? { csvFile: state.csvClientError }
        : {})
    }))

    const clearFieldError = (key) => {
      if (!state.apiFieldErrors[key]) return
      const next = { ...state.apiFieldErrors }
      delete next[key]
      state.apiFieldErrors = next
    }

    const patchForm = (key, value) => {
      state.form[key] = value ?? (key === "studentIds" ? [] : "")
      clearFieldError(key)
      if (
        key === "studentIds" ||
        key === "csvFile" ||
        key === "selectionMethod"
      ) {
        clearFieldError("studentIds")
        clearFieldError("csvFile")
        state.csvClientError = ""
      }
      state.isServerError = false
      state.serverTitle = ""
      state.serverMessage = ""
    }

    const onCsvError = (message) => {
      state.csvClientError = message || ""
      clearFieldError("csvFile")
    }

    const onCancel = () => {
      emit("cancel")
    }

    const onSubmit = async () => {
      state.touched = true
      state.apiFieldErrors = {}
      state.isServerError = false
      state.serverTitle = ""
      state.serverMessage = ""
      if (!state.form.csvFile) {
        state.csvClientError = ""
      }

      if (Object.keys(clientErrors.value).length) return

      try {
        state.isProcessing = true
        const result = await store.dispatch(
          "behaviourManage/createPointsCorrection",
          state.form
        )
        if (isCsvSelection.value) {
          if (result?.isBackgroundJob) {
            toast.info(
              result.message || t("behaviour_manage.csv_file_processing_toast")
            )
          } else {
            if (claimPbisCsvCompletionToast(result?.importLogId)) {
              toast.success(
                result?.message ||
                  t("behaviour_manage.csv_correction_completed_toast", {
                    count: result?.count ?? 0
                  })
              )
            }
            modal?.setAction?.("pointHistoryCorrected", {
              ...state.form,
              ...result
            })
          }
        } else {
          modal?.setAction?.("pointHistoryCorrected", {
            ...state.form,
            ...result
          })
        }
        emit("cancel")
      } catch (error) {
        const parsed = parseBehaviourPointsApiError(error, "correction")
        if (Object.keys(parsed.fieldErrors).length) {
          state.apiFieldErrors = parsed.fieldErrors
        }

        state.isServerError = true
        state.serverTitle = t("behaviour_manage.error_title")
        state.serverMessage =
          parsed.message || t("behaviour_manage.correction_error")
      } finally {
        state.isProcessing = false
      }
    }

    return {
      fieldLabels,
      pointsCorrectedHint,
      state,
      errors,
      patchForm,
      onCsvError,
      onCancel,
      onSubmit
    }
  }
}
</script>
